import { useNavigate, useSearch } from '@tanstack/react-router';
import { Ban, RotateCcw, UsersRound } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import {
  DataTable,
  useDataTable,
  useSearchListState,
  type DataTableColumnDef,
  type FilterDef,
  type SearchPatch,
} from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import type { AdminAuctionRegistration } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { ReadOnlyBanner } from '@/shared/store/read-only-banner';
import { useStore } from '@/shared/store/store-context';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import { AuctionsNav } from '../components/auctions-nav';
import { ReasonDialog } from '../components/reason-dialog';
import { useBlockBidder, useUnblockBidder } from '../hooks/use-auction-mutations';
import { useAuctionRegistrations } from '../hooks/use-auction-registrations';
import { registrationListParams } from '../schemas/auction-list-search';

const ROUTE_ID = '/_authed/_active/s/$storeId/auctions/bidders' as const;
const ROUTE_PATH = '/s/$storeId/auctions/bidders' as const;

function Bidders() {
  const { t } = useTranslation('auctions');
  const format = useFormat();
  const store = useStore();
  const message = useErrorMessage();
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });
  const { editable, reason } = useStoreEditable({ permission: 'auctions.write' });
  const [blocking, setBlocking] = useState<AdminAuctionRegistration | null>(null);
  const block = useBlockBidder(store.id);
  const unblock = useUnblockBidder(store.id);

  const filters = useMemo<Array<FilterDef<AdminAuctionRegistration>>>(
    () => [
      {
        key: 'blocked',
        label: t('bidders.filter'),
        type: 'boolean',
        pinned: true,
        trueLabel: t('bidders.blockedOnly'),
        falseLabel: t('bidders.activeOnly'),
      },
    ],
    [t],
  );
  const onSearchChange = useCallback(
    (patch: SearchPatch) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const list = useSearchListState({ search, onSearchChange, filters });
  const query = useAuctionRegistrations(store.id, registrationListParams(list.params));

  const columns = useMemo<Array<DataTableColumnDef<AdminAuctionRegistration>>>(
    () => [
      {
        id: 'bidder',
        header: t('bidders.columns.bidder'),
        cell: ({ row }) => (
          <span className="flex flex-col">
            <span className="font-medium">
              {row.original.customerName ?? row.original.customerEmail}
            </span>
            <span className="text-muted-foreground text-xs">{row.original.customerEmail}</span>
          </span>
        ),
        meta: { label: t('bidders.columns.bidder'), lockVisibility: true },
        size: 260,
      },
      {
        id: 'status',
        header: t('bidders.columns.status'),
        cell: ({ row }) =>
          row.original.blockedAt ? (
            <span className="flex flex-col gap-0.5">
              <Badge
                variant="outline"
                className="bg-destructive/10 text-destructive w-fit border-transparent"
              >
                {t('bidders.blocked')}
              </Badge>
              {row.original.blockedReason ? (
                <span className="text-muted-foreground text-xs whitespace-normal">
                  {row.original.blockedReason}
                </span>
              ) : null}
            </span>
          ) : (
            <Badge
              variant="outline"
              className="bg-success/15 text-success dark:bg-success/20 border-transparent"
            >
              {t('bidders.active')}
            </Badge>
          ),
        meta: { label: t('bidders.columns.status'), filterKey: 'blocked' },
        size: 200,
      },
      {
        id: 'terms',
        header: t('bidders.columns.terms'),
        cell: ({ row }) => (
          <span title={format.utc(row.original.acceptedAt)}>
            {t('bidders.accepted', {
              version: row.original.termsVersion,
              when: format.date(row.original.acceptedAt),
            })}
          </span>
        ),
        meta: { label: t('bidders.columns.terms') },
        size: 220,
      },
      ...(editable
        ? [
            {
              id: 'actions',
              header: () => <span className="sr-only">{t('bidders.columns.actions')}</span>,
              cell: ({ row }: { row: { original: AdminAuctionRegistration } }) =>
                row.original.blockedAt ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={unblock.isPending}
                    onClick={() =>
                      unblock.mutate(
                        { id: row.original.id, reason: '' },
                        { onError: (error) => toast.error(message(error)) },
                      )
                    }
                  >
                    <RotateCcw aria-hidden />
                    {t('bidders.unblock')}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setBlocking(row.original)}
                  >
                    <Ban aria-hidden />
                    {t('bidders.block')}
                  </Button>
                ),
              meta: { label: t('bidders.columns.actions'), align: 'end' as const },
              size: 120,
            },
          ]
        : []),
    ],
    [editable, format, message, t, unblock],
  );
  const table = useDataTable({
    mode: 'server',
    data: query.data?.items ?? [],
    nextCursor: query.data?.nextCursor,
    list,
    columns,
    filters,
    getRowId: (row) => row.id,
    tableId: 'auction-bidders',
  });

  const narrowed = Object.keys(list.filters).length > 0 || Boolean(list.cursor);
  const isEmpty = query.isSuccess && query.data.items.length === 0 && !narrowed;

  return (
    <PageContent>
      <PageHeader title={t('bidders.title')} description={t('bidders.description')} />
      {reason === 'closed' ? <ReadOnlyBanner reason="closed" /> : null}
      <AuctionsNav storeId={store.id} />
      {isEmpty ? (
        <EmptyState
          icon={UsersRound}
          title={t('bidders.empty.title')}
          description={t('bidders.empty.description')}
        />
      ) : (
        <DataTable
          pillFilter="blocked"
          table={table}
          search={false}
          loading={query.isPending}
          fetching={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          getRowLabel={(row) => row.customerName ?? row.customerEmail}
          resourceName={{
            singular: t('bidders.resource.one'),
            plural: t('bidders.resource.other'),
          }}
          emptyState={{ title: t('noMatches') }}
        />
      )}
      {editable ? (
        <ReasonDialog
          open={blocking !== null}
          onOpenChange={(open) => {
            if (!open) setBlocking(null);
          }}
          title={t('block.title')}
          description={t('block.description', {
            bidder: blocking ? (blocking.customerName ?? blocking.customerEmail) : '',
          })}
          label={t('block.reason')}
          hint={t('block.hint')}
          confirmLabel={t('block.confirm')}
          pending={block.isPending}
          onConfirm={(text) =>
            blocking ? block.mutateAsync({ id: blocking.id, reason: text }) : Promise.resolve()
          }
        />
      ) : null}
    </PageContent>
  );
}

export function AuctionBiddersPage() {
  return (
    <FeatureErrorBoundary name="auctions.bidders">
      <Bidders />
    </FeatureErrorBoundary>
  );
}
